import { storeToRefs } from "pinia";
import type { IEditor } from "board-core";
import { debounce } from "lodash";
import { fabric } from "fabric";
import { watch, type Ref, ref } from "vue";
import { useBoardLayerStore } from "../helper/store/layer";
import { useEditorStore } from "../helper/store/editor";
import { Layer } from "@xmiles/holopix-types";
import { saveBoard, saveBoardLocal, setSaveEnabled } from "../helper/boardSave";
import { generateLayerCid, getNextImageIndex } from "../helper/layerUtils";
import { useMouseEvents } from "../hotkeys/mouseEvents";
import { CANVAS_OBJECT_FIELDS, isPlaceholderObject } from "../../helper/canvasConfig";
import { installSplitCanvasTimeline, splitTimeline } from "../helper/splitCanvasTimeline";
import { recordCanvasObjectInsertion } from "../domain/generationLayout/insertionOrder";

/**
 * 画布编辑器 Hook
 *
 * 【设计目标】
 * 1. 单一职责：专注于画布事件绑定、状态同步和自动保存
 * 2. 历史记录解耦：历史记录功能已抽离到 useCanvasHistory
 * 3. 生命周期管理：清晰的绑定/解绑机制，避免内存泄漏
 * 4. 性能优化：防抖处理、RAF节流、批量操作
 *
 * 【核心功能】
 * - 画布事件监听（对象增删改、鼠标移动、视口变化）
 * - 自动保存（本地缓存 + 服务器）
 * - 选中状态同步
 * - 视口双向同步（Canvas <-> Store）
 */

// 防抖延迟时间配置（毫秒）
const SAVE_LOCAL_DEBOUNCE_MS = 500;
const SAVE_SERVER_DEBOUNCE_MS = 3000;

// 事件监听器存储类型
interface EventListeners {
  canvas: Map<string, (e: any) => void>;
  window: (() => void) | null;
  viewport: (() => void) | null;
}

export const useCanvasEditor = () => {
  // 获取 Store
  const boardLayerStore = useBoardLayerStore();
  const editorStore = useEditorStore();
  const { editor: editorRef } = storeToRefs(editorStore) as unknown as { editor: Ref<IEditor | null> };

  // 本地状态
  const isSyncingViewport = ref(false);
  const isAdjustingSelection = ref(false);
  // 交互状态追踪，用于在拖拽等交互过程中暂停自动保存或避免破坏性操作
  const isInteracting = ref(false);
  const eventListeners = ref<EventListeners>({
    canvas: new Map(),
    window: null,
    viewport: null
  });

  // 防抖函数引用
  const saveLocalDebounceFn = ref<((() => void) & { cancel?: () => void }) | null>(null);
  const saveServerDebounceFn = ref<((() => void) & { cancel?: () => void }) | null>(null);

  // TB-9293：页面隐藏/关闭时立即 flush 防抖保存（处理函数声明在 composable 作用域，
  // 供 bindListeners 注册、unbindListeners 解绑）
  const flushPendingSaves = () => {
    // lodash debounce 实例带 flush；ref 声明类型（cancel 可选）未收录，运行时必然存在
    (saveLocalDebounceFn.value as any)?.flush?.();
    (saveServerDebounceFn.value as any)?.flush?.();
  };
  const onPageHideFlush = () => flushPendingSaves();
  const onVisibilityHiddenFlush = () => {
    if (document.visibilityState === "hidden") flushPendingSaves();
  };

  /**
   * 获取画布实例
   */
  const getCanvas = (): fabric.Canvas | null => {
    return editorRef.value?.fabricCanvas || null;
  };

  /**
   * 同步画布数据到 Store
   * 用于保存前确保数据最新
   * @param force 是否强制同步（忽略交互状态检查）
   */
  const syncCanvasDataToStore = (force = false) => {
    const canvas = getCanvas();
    if (!canvas) return;

    // 如果正在进行交互（如拖拽、缩放），且非强制同步，则跳过
    // 避免打断交互或性能损耗
    if (!force && isInteracting.value) {
      // 诊断：此处跳过会让 generateSnapshot 读到的 store 缓存滞后于实时画布（历史快照可能缺新对象）
      splitTimeline("sync-skip-interacting", {}, { silent: true });
      return;
    }

    // 【关键修复】在序列化之前，先记录并取消 ActiveSelection
    // 问题背景：当多选对象处于 ActiveSelection 中时，子对象的坐标是相对于 ActiveSelection 的局部坐标
    // 虽然 Fabric.js 的 toJSON 会自动转换坐标，但在某些情况下（如快速连续操作），
    // 坐标转换可能不完整，导致撤销/重做后位置偏移
    const activeObject = canvas.getActiveObject();
    const hadActiveSelection = activeObject && activeObject.type === "activeSelection";
    const selectedIds: string[] = hadActiveSelection
      ? (activeObject as fabric.ActiveSelection).getObjects().map((obj: any) => obj.id).filter(Boolean)
      : activeObject
        ? [(activeObject as any).id].filter(Boolean)
        : [];

    // 取消选中状态，确保对象在世界坐标系中被正确序列化
    if (hadActiveSelection) {
      canvas.discardActiveObject();
    }

    // 序列化画布数据，包含自定义属性
    const json = canvas.toJSON([...CANVAS_OBJECT_FIELDS]);
    boardLayerStore.setFabricCanvasData(json);

    // 恢复之前的选中状态
    if (selectedIds.length > 0) {
      const objectsToSelect: fabric.Object[] = [];
      selectedIds.forEach((id) => {
        const obj = canvas.getObjects().find((o: any) => o.id === id);
        if (obj) {
          objectsToSelect.push(obj);
        }
      });

      if (objectsToSelect.length > 0) {
        if (objectsToSelect.length === 1) {
          canvas.setActiveObject(objectsToSelect[0]);
          objectsToSelect[0].setCoords();
        } else {
          const newSelection = new fabric.ActiveSelection(objectsToSelect, { canvas });
          canvas.setActiveObject(newSelection);
          newSelection.setCoords();
        }
        canvas.requestRenderAll();
      }
    }
  };

  /**
   * 同步选中状态到 Store
   */
  const syncSelectionState = () => {
    const canvas = getCanvas();
    const editor = editorRef.value;
    if (!canvas || !editor) return;

    const activeObjects = canvas.getActiveObjects();
    const activeObjectIds = activeObjects
      .map((obj) => {
        // 过滤临时图层
        if ((obj as any).temporaryLayerInfo) return null;
        return (obj as Layer).id;
      })
      .filter(Boolean) as string[];

    boardLayerStore.setSelectedLayerCids(activeObjectIds);

    // 设置当前交互对象ID
    if (activeObjectIds.length > 0) {
      editor.setCurrentInteractionObjectId?.(activeObjectIds[0]);
    }
  };

  const filterPlaceholdersFromActiveSelection = (canvas: fabric.Canvas) => {
    const activeObject = canvas.getActiveObject();
    if (!activeObject || activeObject.type !== "activeSelection") return;

    const selection = activeObject as fabric.ActiveSelection;
    const selectedObjects = selection.getObjects();
    if (selectedObjects.length === 0) return;

    const nonPlaceholderObjects = selectedObjects.filter((obj) => !isPlaceholderObject(obj));
    if (nonPlaceholderObjects.length === selectedObjects.length) return;

    isAdjustingSelection.value = true;
    try {
      canvas.discardActiveObject();

      if (nonPlaceholderObjects.length === 1) {
        const obj = nonPlaceholderObjects[0];
        canvas.setActiveObject(obj);
        obj.setCoords();
      } else if (nonPlaceholderObjects.length > 1) {
        const nextSelection = new fabric.ActiveSelection(nonPlaceholderObjects, { canvas });
        canvas.setActiveObject(nextSelection);
        nextSelection.setCoords();
      }

      canvas.requestRenderAll();
    } finally {
      isAdjustingSelection.value = false;
    }
  };

  const handleSelectionChanged = (_e: fabric.IEvent) => {
    const canvas = getCanvas();
    if (!canvas) return;
    if (isAdjustingSelection.value) return;

    filterPlaceholdersFromActiveSelection(canvas);
    syncSelectionState();
  };

  /**
   * 处理对象添加事件
   * 1. 为新对象生成 ID 和名称
   * 2. 触发自动保存
   */
  const handleObjectAdded = (e: fabric.IEvent) => {
    const obj = e.target;
    if (!obj) return;

    // 过滤掉临时对象（如 Ghost 对象、辅助线等）
    // 避免在拖拽复制过程中频繁触发保存，打断交互
    if ((obj as any).isGhost || (obj as any).excludeFromExport || (obj as any).temporary) {
      return;
    }

    // 检查对象是否已有 id 和 name，如果没有则自动生成
    if (!(obj as any).id) {
      (obj as any).id = generateLayerCid();
    }
    if (!(obj as any).name) {
      const canvas = editorRef.value?.fabricCanvas;
      (obj as any).name = canvas ? `图片${getNextImageIndex(canvas, "图片")}` : "图片1";
    }

    const canvas = getCanvas();
    if (canvas) recordCanvasObjectInsertion(canvas, obj);

    // 真实对象添加时强制同步到 Store，确保历史记录能捕捉到变更
    // （预览线等临时对象已通过 isGhost/excludeFromExport/temporary 在上方过滤）
    triggerAutoSave(true);
  };

  /**
   * 处理对象修改事件
   */
  const handleObjectModified = () => {
    // object:modified 意味着一次变换操作结束，必须强制同步
    triggerAutoSave(true);
  };

  /**
   * 处理对象删除事件
   */
  const handleObjectRemoved = (e: fabric.IEvent) => {
    const obj = e.target;
    // 同样过滤掉临时对象的移除事件
    if (obj && ((obj as any).isGhost || (obj as any).excludeFromExport || (obj as any).temporary)) {
      return;
    }
    // 占位符删除时强制同步，确保 overlay 立即更新，避免已删除占位符的 div 残留
    const forceSync = obj && (obj as any).isPlaceholder;
    triggerAutoSave(forceSync);
  };

  /**
   * 触发自动保存
   * 同时触发本地缓存和服务器保存（防抖）
   * @param force 是否强制同步
   */
  const triggerAutoSave = (force = false) => {
    // 同步数据到 Store
    syncCanvasDataToStore(force);

    // 触发保存（防抖）
    saveLocalDebounceFn.value?.();
    saveServerDebounceFn.value?.();
  };

  /**
   * 创建视口同步处理器
   */
  const createViewportSyncHandler = (canvas: fabric.Canvas) => {
    return () => {
      if (isSyncingViewport.value) return;

      const zoom = canvas.getZoom();
      const vpt = canvas.viewportTransform;
      if (!vpt) return;

      isSyncingViewport.value = true;
      try {
        boardLayerStore.updateViewportConfig({
          zoom: parseFloat((zoom * 100).toFixed(2)),
          pan: { x: vpt[4], y: vpt[5] }
        });
      } finally {
        isSyncingViewport.value = false;
      }
    };
  };

  /**
   * 绑定画布事件监听器
   */
  const bindCanvasListeners = () => {
    const editor = editorRef.value;
    if (!editor?.fabricCanvas) {
      console.warn("[useCanvasEditor] Canvas editor not initialized, cannot bind listeners");
      return;
    }

    const canvas = editor.fabricCanvas;

    // 拆分图组诊断时间线（幂等安装）：object:removed 全量入 localStorage，跨刷新可回放
    installSplitCanvasTimeline(canvas);

    // 先解绑旧的监听器，防止重复绑定
    unbindListeners();

    // 启用保存功能
    setSaveEnabled(true);

    // 初始化防抖保存函数
    saveLocalDebounceFn.value = debounce(saveBoardLocal, SAVE_LOCAL_DEBOUNCE_MS);
    saveServerDebounceFn.value = debounce(saveBoard, SAVE_SERVER_DEBOUNCE_MS);

    // TB-9293：注册保存 flush 监听（处理函数在 composable 作用域声明）
    window.addEventListener("pagehide", onPageHideFlush);
    document.addEventListener("visibilitychange", onVisibilityHiddenFlush);

    // 绑定鼠标事件（缩放、中键拖拽等）
    const mouseEvents = useMouseEvents(canvas);
    const mouseListeners = mouseEvents.bindFabricEvents();
    
    // 绑定窗口事件，并确保在全局鼠标松开时重置交互状态
    const mouseEventsCleanup = mouseEvents.bindWindowEvents();
    const globalMouseUpHandler = () => {
      isInteracting.value = false;
    };
    window.addEventListener("mouseup", globalMouseUpHandler);

    eventListeners.value.window = () => {
      window.removeEventListener("mouseup", globalMouseUpHandler);
      mouseEventsCleanup?.();
    };

    // 创建视口同步处理器
    let lastViewportZoom = canvas.getZoom();
    let lastViewportPanX = canvas.viewportTransform?.[4] ?? 0;
    let lastViewportPanY = canvas.viewportTransform?.[5] ?? 0;

    const syncViewportSignatureFromCanvas = () => {
      lastViewportZoom = canvas.getZoom();
      const vpt = canvas.viewportTransform;
      if (!vpt) return;
      lastViewportPanX = vpt[4];
      lastViewportPanY = vpt[5];
    };

    const viewportSyncHandlerBase = createViewportSyncHandler(canvas);
    const viewportSyncHandler = () => {
      viewportSyncHandlerBase();
      syncViewportSignatureFromCanvas();
    };

    let afterRenderScheduled = false;
    const afterRenderViewportDetector = () => {
      if (afterRenderScheduled) return;
      afterRenderScheduled = true;
      requestAnimationFrame(() => {
        afterRenderScheduled = false;

        const vpt = canvas.viewportTransform;
        if (!vpt) return;

        const zoom = canvas.getZoom();
        const panX = vpt[4];
        const panY = vpt[5];

        const zoomChanged = Math.abs(zoom - lastViewportZoom) > 0.0001;
        const panChanged = Math.abs(panX - lastViewportPanX) > 0.1 || Math.abs(panY - lastViewportPanY) > 0.1;

        if (!zoomChanged && !panChanged) return;

        syncViewportSignatureFromCanvas();
        canvas.fire("viewport:transform", { e: null });
      });
    };

    // 收集所有画布事件处理器
    const handlers = new Map<string, (e: any) => void>();

    // 对象事件
    handlers.set("object:added", handleObjectAdded);
    handlers.set("object:modified", handleObjectModified);
    handlers.set("object:removed", handleObjectRemoved);

    // 选中事件
    handlers.set("selection:created", handleSelectionChanged);
    handlers.set("selection:updated", handleSelectionChanged);
    handlers.set("selection:cleared", handleSelectionChanged);

    // 合并鼠标事件处理器
    // 注意：我们需要拦截 mouse:down 和 mouse:up 来维护 isInteracting 状态
    Object.entries(mouseListeners).forEach(([eventType, handler]) => {
      if (eventType === "mouse:down") {
        handlers.set(eventType, (e) => {
          isInteracting.value = true;
          handler(e);
        });
      } else if (eventType === "mouse:up") {
        handlers.set(eventType, (e) => {
          isInteracting.value = false;
          handler(e);
        });
      } else {
        handlers.set(eventType, handler);
      }
    });

    // 视口事件
    handlers.set("viewport:transform", viewportSyncHandler);
    handlers.set("after:render", afterRenderViewportDetector);

    // 注册到画布
    handlers.forEach((handler, eventType) => {
      canvas.on(eventType, handler);
    });

    eventListeners.value.canvas = handlers;

    // 监听 Store 视口变化同步到 Canvas
    eventListeners.value.viewport = watch(
      () => boardLayerStore.viewport,
      (newViewport) => {
        if (isSyncingViewport.value || !canvas) return;

        isSyncingViewport.value = true;
        try {
          const currentZoom = canvas.getZoom();
          const currentVpt = canvas.viewportTransform || [1, 0, 0, 1, 0, 0];
          const newZoom = (newViewport as any).zoom / 100;
          const newPan = (newViewport as any).pan;

          let changed = false;

          // 检查缩放变化
          if (Math.abs(currentZoom - newZoom) > 0.0001) {
            canvas.setZoom(newZoom);
            changed = true;
          }

          // 检查平移变化
          if (Math.abs(currentVpt[4] - newPan.x) > 0.1 || Math.abs(currentVpt[5] - newPan.y) > 0.1) {
            const vpt = canvas.viewportTransform;
            if (vpt) {
              vpt[4] = newPan.x;
              vpt[5] = newPan.y;
              canvas.setViewportTransform(vpt);
              changed = true;
            }
          }

          if (changed) {
            canvas.requestRenderAll();
            canvas.fire("viewport:transform", { e: null });
          }
        } finally {
          isSyncingViewport.value = false;
        }
      },
      { deep: true, immediate: true }
    );
  };

  /**
   * 解绑所有事件监听器
   */
  const unbindListeners = () => {
    const canvas = getCanvas();

    // 禁用保存功能
    setSaveEnabled(false);

    // 取消待执行的防抖函数
    saveLocalDebounceFn.value?.cancel?.();
    saveServerDebounceFn.value?.cancel?.();
    saveLocalDebounceFn.value = null;
    saveServerDebounceFn.value = null;

    // 移除画布事件监听器
    if (canvas) {
      eventListeners.value.canvas.forEach((handler, eventType) => {
        canvas.off(eventType, handler);
      });
    }
    eventListeners.value.canvas.clear();

    // 解绑窗口事件
    eventListeners.value.window?.();
    eventListeners.value.window = null;

    // 移除保存 flush 监听（TB-9293）
    window.removeEventListener("pagehide", onPageHideFlush);
    document.removeEventListener("visibilitychange", onVisibilityHiddenFlush);

    // 解绑视口监听
    eventListeners.value.viewport?.();
    eventListeners.value.viewport = null;
  };

  /**
   * 清理资源
   */
  const cleanup = () => {
    unbindListeners();
    editorStore.setSyncingState(false);
  };

  /**
   * 设置编辑器实例
   * @param editor - 编辑器实例或 null
   */
  const setEditor = (editor: IEditor | null) => {
    cleanup();
    editorStore.setEditor(editor);
  };

  /**
   * 获取编辑器实例
   */
  const getEditor = (): IEditor | null => {
    return editorRef.value;
  };

  /**
   * 销毁编辑器
   */
  const destroyEditor = () => {
    cleanup();
    editorStore.setEditor(null);
  };

  return {
    // 编辑器引用
    editorRef,

    // 核心方法
    setEditor,
    getEditor,
    destroyEditor,

    // 状态同步
    syncSelectionState,

    // 事件绑定
    bindCanvasListeners,
    unbindListeners
  };
};

export default useCanvasEditor;
